import { AnchorButton, Button, Callout, Card, Divider, Elevation, Intent, Switch, Tooltip } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'
import * as React from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import Client from '../../../../../../client'
import { ElementJoiner } from '../../../../../../components/ElementJoiner'
import APIErrorCallout from '../../../../../../components/errors/APIErrorCallout'
import Channels from '../../../../../../constants/Channels'
import { usePermissions } from '../../../../../../hooks/usePermissions'
import { AppState } from '../../../../../../store'
import { UprightStyle } from '../../../../../../types'
import '../../style.scss'
import { Listing } from '../../types'
import { resolveConcurrentSwitchState } from '../../utils'
import { ChannelCard } from '../ChannelCard'
import { ListingInfo } from '../ListingInfo'
import { MarketAccountLite } from '../../../../v2/types'
import CreateListingButtonOrLink from '../CreateListingButtonOrLink'

const styles: UprightStyle = {
  currentStepCallout: {
    paddingBottom: 15
  },
  stepCard: {
    padding: 10,
    textAlign: 'left'
  },
  channelSwitch: {
    margin: '5px 0',
    // make sure the hit target encompasses the switch + label text only
    width: 'fit-content',
    maxWidth: '100%'
  }
}

const DELIST_ERROR = { data: { errors: { listing: ['could not be delisted'] } }, status: 422 }

interface Props {
  productId: number
  supplier?: { marketAccounts: MarketAccountLite[] }
  canonListings: Listing[]
  reload: (setLoadingIndicator?: boolean) => void
  openCreateListingWhichAccountDialog: (marketId: number, additionalQueryParams: {}) => void
}

export const ConcurrentListingControls: React.FC<Props> = props => {
  const [channelToggleState, setChannelToggleState] = React.useState({})

  const [errors, setErrors] = React.useState(null)

  const onToggleSelectedChannel = async (channel, listing) => {
    if (!channel) return
    const toggleState = channel && channelToggleState[channel.id]
    if (toggleState.disabled) return

    const setCurrentChannelToggle = data =>
      setChannelToggleState({
        ...channelToggleState,
        [channel.id]: { ...toggleState, ...data }
      })

    const resetCurrentChannelToggle = () => setCurrentChannelToggle({})

    setCurrentChannelToggle({ isOn: !toggleState.isOn, disabled: true })

    try {
      if (toggleState.isOn) {
        // DELIST if already listed concurrently
        const res = await Client.bulkEndListings(channel, [listing.listingId])
        const failedIds: number[] = res?.data?.failed || []
        if (failedIds.includes(listing.listingId)) {
          resetCurrentChannelToggle()
          setErrors(DELIST_ERROR)
        } else {
          setCurrentChannelToggle({ isOn: false, disabled: true })
          setErrors(null)
          props.reload(false)
        }
      } else {
        // List/Relist as concurrent if not published
        await Client.v5ProductAddConcurrent(props.productId, channel.id)
        setCurrentChannelToggle({ isOn: true, disabled: true })
        setErrors(null)
        props.reload(false)
      }
    } catch (error) {
      resetCurrentChannelToggle()
      setErrors(error.response)
    }
  }

  React.useEffect(() => {
    setChannelToggleState(
      props.canonListings.reduce(
        (acc, curr) => ({
          ...acc,
          [curr.marketId]: resolveConcurrentSwitchState(curr)
        }),
        {}
      )
    )
  }, [props.canonListings])

  const connectedMarkets = useSelector((s: AppState) => s.current.organization.connectedMarkets)

  const onboardedChannels = useSelector((s: AppState) =>
    // assumption: we only allow each channel to have a single canon listing.

    // We will only show the "Create listing" button if there is at least one onboarded market account for a given channel.
    // We filter out non-onboarded accounts because... that's how we originally built it. The listing flow
    // could break if the account was temporarily non-onboarded and we let them attempt to create a listing,
    // because some inputs on the listing form (like ebay store category) probably make API calls that authenticate with the market account.

    s.current.organization.uniqueConnectedMarkets.filter(
      m => (m.onboardedSummary === 'PARTIAL' || m.onboardedSummary === 'YES') && m.id !== Channels.BLUEBOX.id
    )
  )
  const entries = React.useMemo(
    () =>
      onboardedChannels
        .map(channel => ({
          channel,
          listing: props.canonListings.find(listing => listing?.marketId === channel.id)
        }))
        .sort((a, b) => a.channel.id - b.channel.id),
    [props.canonListings, onboardedChannels]
  )
  const { permissions } = usePermissions()
  return (
    <Card elevation={Elevation.THREE} style={styles.stepCard}>
      <div style={styles.currentStepCallout}>
        <Callout title="Concurrent step" icon={IconNames.SEND_TO_GRAPH}>
          Use the switches below to control where the product is listed
        </Callout>
      </div>
      {errors && (
        <div style={styles.currentStepCallout}>
          <APIErrorCallout response={errors} />
        </div>
      )}
      <ElementJoiner separator={<Divider />}>
        {entries.map(({ channel, listing }) => {
          const switchState = channelToggleState[channel.id]
          return (
            <ChannelCard channelId={channel.id} key={`concurrent-listing-${channel?.id}`}>
              <div style={{ alignSelf: 'center' }}>
                <b>{channel.humanReadableName}</b>
                {listing && switchState && (
                  <>
                    {permissions.includes('listings.manage') && (
                      <Switch
                        checked={switchState.isOn}
                        onChange={() => onToggleSelectedChannel(channel, listing)}
                        style={styles.channelSwitch}
                        disabled={switchState.disabled}
                      >
                        {switchState.message || 'List concurrently'}
                      </Switch>
                    )}
                    <ListingInfo listing={listing} />
                  </>
                )}
              </div>

              {!listing && (
                <CreateListingButtonOrLink
                  channel={channel}
                  connectedMarkets={connectedMarkets}
                  supplier={props.supplier}
                  productId={props.productId}
                  openCreateListingWhichAccountDialog={props.openCreateListingWhichAccountDialog}
                  additionalQueryParams={{ validate_concurrent: true }}
                />
              )}
              {listing && listing.marketId !== Channels.OFFER_UP.id && permissions.includes('products.post') && (
                <Link to={`/listings/${channel.name}/${listing.listingId}/edit?validate_concurrent=true`}>
                  <Button text="Edit" minimal intent={Intent.PRIMARY} />
                </Link>
              )}
              {listing && listing.marketId === Channels.OFFER_UP.id && permissions.includes('products.post') && (
                <Tooltip content="Channel no longer available">
                  <AnchorButton disabled text="Edit" minimal intent={Intent.PRIMARY} />
                </Tooltip>
              )}
            </ChannelCard>
          )
        })}
      </ElementJoiner>
    </Card>
  )
}
